import './App.css';
import { useState, useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import experiences from '../src/data/experiences.json';
import projects from '../src/data/projects.json';
import books from '../src/data/books.json';
import works from '../src/data/works.json';
import gb from './data/gb.jpg';

import { storage } from './firebase';
import { ref, getDownloadURL } from 'firebase/storage';

// Icons as inline SVGs
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const GithubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
  </svg>
);

const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// Theme definitions
const themes = [
  { id: 'crimson', name: 'Crimson', color: '#ef4444' },
  { id: 'cobalt', name: 'Cobalt', color: '#3b82f6' },
  { id: 'emerald', name: 'Emerald', color: '#10b981' },
  { id: 'violet', name: 'Violet', color: '#8b5cf6' },
  { id: 'amber', name: 'Amber', color: '#f59e0b' },
];

const interests = ["Machine Learning", "Data", "Art", "UI/UX", "Gaming", "Economics", "Generative Systems", "Manufacturing", "Startups"];

interface BookContent {
  [key: string]: string;
}

interface BookPages {
  [key: string]: string[];
}

interface WorkItem {
  type: 'image' | 'video';
  src: string;
  caption: string;
}

interface WorkProject {
  id: string;
  title: string;
  description: string;
  coverIndex: number;
  works: WorkItem[];
}

function App() {
  const [theme, setTheme] = useState('crimson');
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [bookContent, setBookContent] = useState<BookContent>({});
  const [pages, setPages] = useState<BookPages>({});
  const [currentPageIndex, setCurrentPageIndex] = useState<{ [key: string]: number }>({});
  const [activeNote, setActiveNote] = useState<string | null>(null);
  const [activeWork, setActiveWork] = useState<{ projectId: string; workIndex: number } | null>(null);
  const [workUrls, setWorkUrls] = useState<{ [projectId: string]: string[] }>({});
  const themeRef = useRef<HTMLDivElement>(null);

  // Close theme menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (themeRef.current && !themeRef.current.contains(event.target as Node)) {
        setThemeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Fetch book content
  useEffect(() => {
    books.forEach(book => {
      async function fetchBooksContent() {
        try {
          const contentRef = ref(storage, book.content);
          const contentUrl = await getDownloadURL(contentRef);
          const response = await fetch(contentUrl);
          const markdown = await response.text();
          setBookContent(prev => ({ ...prev, [book.id]: markdown }));

          const pagesUrls = await Promise.all(
            book.pages.map(page => getDownloadURL(ref(storage, page)))
          );
          setPages(prev => ({ ...prev, [book.id]: pagesUrls }));
          setCurrentPageIndex(prev => ({ ...prev, [book.id]: 0 }));
        } catch (error) {
          console.error('Error fetching book content:', error);
        }
      }
      fetchBooksContent();
    });
  }, []);

  // Fetch work URLs from Firebase Storage
  useEffect(() => {
    (works as WorkProject[]).forEach(project => {
      async function fetchWorkUrls() {
        try {
          const urls = await Promise.all(
            project.works.map(async (work) => {
              // Handle gs:// URLs
              if (work.src.startsWith('gs://')) {
                const storageRef = ref(storage, work.src);
                return getDownloadURL(storageRef);
              }
              return work.src;
            })
          );
          setWorkUrls(prev => ({ ...prev, [project.id]: urls }));
        } catch (error) {
          console.error('Error fetching work URLs:', error);
        }
      }
      fetchWorkUrls();
    });
  }, []);

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
    setThemeMenuOpen(false);
  };

  const handleNavClick = () => {
    setMobileNavOpen(false);
  };

  const nextPage = (bookId: string) => {
    const maxIndex = (pages[bookId]?.length || 1) - 1;
    setCurrentPageIndex(prev => ({
      ...prev,
      [bookId]: Math.min((prev[bookId] || 0) + 1, maxIndex)
    }));
  };

  const prevPage = (bookId: string) => {
    setCurrentPageIndex(prev => ({
      ...prev,
      [bookId]: Math.max((prev[bookId] || 0) - 1, 0)
    }));
  };

  const currentTheme = themes.find(t => t.id === theme) || themes[0];

  // Cycling interest for hero display
  const [currentInterestIndex, setCurrentInterestIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInterestIndex((prev) => (prev + 1) % interests.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard navigation for works modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeWork) return;

      const project = (works as WorkProject[]).find(w => w.id === activeWork.projectId);
      if (!project) return;

      if (e.key === 'Escape') {
        setActiveWork(null);
      } else if (e.key === 'ArrowLeft' && activeWork.workIndex > 0) {
        setActiveWork({ ...activeWork, workIndex: activeWork.workIndex - 1 });
      } else if (e.key === 'ArrowRight' && activeWork.workIndex < project.works.length - 1) {
        setActiveWork({ ...activeWork, workIndex: activeWork.workIndex + 1 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeWork]);

  // Keyboard navigation for notes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeNote) return;

      const book = books.find(b => b.id === activeNote);
      if (!book) return;

      const bookPages = pages[book.id] || [];
      const currentIdx = currentPageIndex[book.id] || 0;

      if (e.key === 'Escape') {
        setActiveNote(null);
      } else if (e.key === 'ArrowLeft' && currentIdx > 0) {
        setCurrentPageIndex(prev => ({ ...prev, [book.id]: currentIdx - 1 }));
      } else if (e.key === 'ArrowRight' && currentIdx < bookPages.length - 1) {
        setCurrentPageIndex(prev => ({ ...prev, [book.id]: currentIdx + 1 }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeNote, pages, currentPageIndex]);

  return (
    <>
      {/* Navigation */}
      <nav className="nav">
        <a href="#" className="nav-brand">Grant Burke</a>

        <div className={`nav-links ${mobileNavOpen ? 'open' : ''}`}>
          <a href="#about" className="nav-link" onClick={handleNavClick}>About</a>
          <a href="#works" className="nav-link" onClick={handleNavClick}>Works</a>
          <a href="#experience" className="nav-link" onClick={handleNavClick}>Experience</a>
          <a href="#projects" className="nav-link" onClick={handleNavClick}>Projects</a>
          <a href="#notes" className="nav-link" onClick={handleNavClick}>Notes</a>
          <a href="#connect" className="nav-link" onClick={handleNavClick}>Connect</a>
        </div>

        <div className="nav-right">
          {/* Theme Switcher */}
          <div className="theme-switcher" ref={themeRef}>
            <button
              className="theme-toggle"
              onClick={() => setThemeMenuOpen(!themeMenuOpen)}
            >
              <span className="theme-dot" style={{ background: currentTheme.color }}></span>
              {currentTheme.name}
            </button>
            <div className={`theme-menu ${themeMenuOpen ? 'open' : ''}`}>
              {themes.map(t => (
                <div
                  key={t.id}
                  className={`theme-option ${theme === t.id ? 'active' : ''}`}
                  onClick={() => handleThemeChange(t.id)}
                >
                  <span className="theme-color" style={{ background: t.color }}></span>
                  {t.name}
                </div>
              ))}
            </div>
          </div>

          <button className="nav-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)}>
            <MenuIcon />
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="app">
        {/* Hero Section */}
        <section className="hero" id="about">
          <div className="hero-header">
            <img src={gb} alt="Grant Burke" className="hero-avatar" />
            <div className="hero-name-container">
              <h1 className="hero-name">Grant Burke</h1>
              <span className="hero-interest-flash" key={currentInterestIndex}>
                {interests[currentInterestIndex]}
              </span>
            </div>
          </div>
          <p className="hero-description">
            Hey, welcome to my site. Look around, say whats up. Some stuff about me: I like to read, play games, and hang out with my friends and family.
            I also enjoy being a soundboard for new ideas. The energy of people working on new things is infectious.
            Recently, I have been playing with generative media as a creative outlet.
          </p>
          <p className="hero-description">
            At work, I like building data systems for business and helping teams bring their ideas to life.
            I am passionate about building systems that help businesses make better decisions.
          </p>


          <div className="social-links">
            <a href="https://twitter.com/granttburke" target="_blank" rel="noopener noreferrer" className="social-link">
              <TwitterIcon />
            </a>
            <a href="https://github.com/gtb123" target="_blank" rel="noopener noreferrer" className="social-link">
              <GithubIcon />
            </a>
            <a href="https://linkedin.com/in/granttburke" target="_blank" rel="noopener noreferrer" className="social-link">
              <LinkedinIcon />
            </a>
            <a href="mailto:grantburke123@gmail.com" className="social-link">
              <MailIcon />
            </a>
          </div>
        </section>

        {/* Selected Works Section */}
        <section className="works-section" id="works">
          <h2 className="section-header">Selected Works</h2>
          <div className="works-grid">
            {(works as WorkProject[]).map((project) => (
              <div
                key={project.id}
                className="work-card"
                onClick={() => setActiveWork({ projectId: project.id, workIndex: 0 })}
              >
                {workUrls[project.id]?.[project.coverIndex] ? (
                  <img
                    src={workUrls[project.id][project.coverIndex]}
                    alt={project.title}
                    className="work-card-image"
                  />
                ) : (
                  <div className="work-card-placeholder">
                    <span className="work-card-icon">◇</span>
                  </div>
                )}
                <div className="work-card-overlay">
                  <h3 className="work-card-title">{project.title}</h3>
                  <span className="work-card-count">{project.works.length} pieces</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Works Modal */}
        <div
          className={`works-modal-overlay ${activeWork ? 'open' : ''}`}
          onClick={() => setActiveWork(null)}
        />
        {activeWork && (() => {
          const project = (works as WorkProject[]).find(w => w.id === activeWork.projectId);
          if (!project) return null;
          const currentWork = project.works[activeWork.workIndex];
          const hasPrev = activeWork.workIndex > 0;
          const hasNext = activeWork.workIndex < project.works.length - 1;

          return (
            <div className={`works-modal ${activeWork ? 'open' : ''}`}>
              <button className="works-modal-close" onClick={() => setActiveWork(null)}>×</button>

              <div className="works-modal-content">
                <div className="works-modal-media">
                  {currentWork.type === 'video' ? (
                    <video
                      src={workUrls[project.id]?.[activeWork.workIndex] || currentWork.src}
                      controls
                      autoPlay
                      loop
                      muted
                      className="works-modal-video"
                    />
                  ) : workUrls[project.id]?.[activeWork.workIndex] ? (
                    <img
                      src={workUrls[project.id][activeWork.workIndex]}
                      alt={currentWork.caption}
                      className="works-modal-image"
                    />
                  ) : (
                    <div className="works-modal-image-placeholder">
                      <span className="works-modal-placeholder-icon">◇</span>
                      <span className="works-modal-placeholder-text">Loading...</span>
                    </div>
                  )}

                  {/* Navigation Arrows */}
                  <div className="works-modal-nav">
                    <button
                      className={`works-modal-arrow ${!hasPrev ? 'disabled' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasPrev) setActiveWork({ ...activeWork, workIndex: activeWork.workIndex - 1 });
                      }}
                    >
                      ‹
                    </button>
                    <button
                      className={`works-modal-arrow ${!hasNext ? 'disabled' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasNext) setActiveWork({ ...activeWork, workIndex: activeWork.workIndex + 1 });
                      }}
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div className="works-modal-info">
                  <h3 className="works-modal-title">{project.title}</h3>
                  <p className="works-modal-caption">{currentWork.caption}</p>
                  <p className="works-modal-description">{project.description}</p>
                  <div className="works-modal-counter">
                    {activeWork.workIndex + 1} / {project.works.length}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Experience Section */}
        <section className="section" id="experience">
          <h2 className="section-header">Experience</h2>
          <div className="card-list">
            {experiences.map((exp) => (
              <div key={exp.companyName} className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <a href={exp.companyURL} target="_blank" rel="noopener noreferrer" className="card-link">
                      {exp.companyName}
                      <span className="card-link-arrow"> ↗</span>
                    </a>
                  </h3>
                  <span className="card-meta">{exp.yearStart} — {exp.yearEnd}</span>
                </div>
                <p className="card-position">{exp.position}</p>
                <p className="card-description">{exp.description}</p>
                {exp.technologies && (
                  <div className="card-tags">
                    {exp.technologies.map((tech) => (
                      <span key={tech} className="tag">{tech}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Projects Section */}
        <section className="section" id="projects">
          <h2 className="section-header">Projects</h2>
          <div className="card-list">
            {projects.map((project) => (
              <div key={project.projectName} className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <a href={project.projectURL} target="_blank" rel="noopener noreferrer" className="card-link">
                      {project.projectName}
                      <span className="card-link-arrow"> ↗</span>
                    </a>
                  </h3>
                </div>
                <p className="card-description">{project.projectDescription}</p>
                {project.technologies && (
                  <div className="card-tags">
                    {project.technologies.map((tech) => (
                      <span key={tech} className="tag">{tech}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Notes Section */}
        <section className="section" id="notes">
          <h2 className="section-header">Notes</h2>
          <p className="hero-description" style={{ marginBottom: 'var(--space-lg)' }}>
            Notes typically come from books I read, podcasts, classes, and other material.
            Topics range from data, UI/UX, and business.
          </p>
          <div className="notes-grid">
            {books.map((book) => (
              <div
                key={book.id}
                className="note-card"
                onClick={() => setActiveNote(book.id)}
              >
                {pages[book.id] && pages[book.id].length > 0 && (
                  <img
                    src={pages[book.id][0]}
                    alt={book.title}
                    className="note-card-image"
                  />
                )}
                <div className="note-card-overlay">
                  <h3 className="note-card-title">{book.title}</h3>
                  <div className="note-card-tags">
                    {book.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
                <span className="note-card-hint">Click to read</span>
              </div>
            ))}
          </div>
        </section>

        {/* Notes Modal */}
        <div
          className={`note-modal-overlay ${activeNote ? 'open' : ''}`}
          onClick={() => setActiveNote(null)}
        />
        {activeNote && (() => {
          const book = books.find(b => b.id === activeNote);
          if (!book) return null;
          const bookPages = pages[book.id] || [];
          const currentIdx = currentPageIndex[book.id] || 0;

          return (
            <div className={`note-modal ${activeNote ? 'open' : ''}`}>
              <button className="note-modal-close" onClick={() => setActiveNote(null)}>×</button>

              <div className="note-modal-content">
                {/* Left side - Image */}
                <div className="note-modal-media">
                  {bookPages.length > 0 ? (
                    <>
                      <img
                        src={bookPages[currentIdx]}
                        alt={book.title}
                        className="note-modal-image"
                      />
                      {bookPages.length > 1 && (
                        <>
                          <div className="note-modal-nav">
                            <button
                              className={`note-modal-arrow ${currentIdx === 0 ? 'disabled' : ''}`}
                              onClick={(e) => { e.stopPropagation(); prevPage(book.id); }}
                            >
                              ‹
                            </button>
                            <button
                              className={`note-modal-arrow ${currentIdx === bookPages.length - 1 ? 'disabled' : ''}`}
                              onClick={(e) => { e.stopPropagation(); nextPage(book.id); }}
                            >
                              ›
                            </button>
                          </div>
                          <div className="note-modal-dots">
                            {bookPages.map((_, idx) => (
                              <span
                                key={idx}
                                className={`note-modal-dot ${idx === currentIdx ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentPageIndex(prev => ({ ...prev, [book.id]: idx }));
                                }}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="note-modal-placeholder">
                      <span className="note-modal-placeholder-icon">◇</span>
                    </div>
                  )}
                </div>

                {/* Right side - Text Content */}
                <div className="note-modal-text">
                  <div className="note-modal-header">
                    <h2 className="note-modal-title">{book.title}</h2>
                    <div className="note-modal-tags">
                      {book.tags.map((tag) => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="note-modal-body">
                    {bookContent[book.id] ? (
                      <Markdown remarkPlugins={[remarkGfm]}>{bookContent[book.id]}</Markdown>
                    ) : (
                      <p style={{ color: 'var(--text-muted)' }}>Loading notes...</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Connect Section */}
        <section className="section" id="connect">
          <h2 className="section-header">Connect</h2>
          <div className="connect-grid">
            <a href="https://twitter.com/granttburke" target="_blank" rel="noopener noreferrer" className="connect-link">
              <TwitterIcon />
              <span className="connect-link-text">X / Twitter</span>
              <span className="connect-link-arrow">↗</span>
            </a>
            <a href="https://github.com/gtb123" target="_blank" rel="noopener noreferrer" className="connect-link">
              <GithubIcon />
              <span className="connect-link-text">GitHub</span>
              <span className="connect-link-arrow">↗</span>
            </a>
            <a href="https://linkedin.com/in/granttburke" target="_blank" rel="noopener noreferrer" className="connect-link">
              <LinkedinIcon />
              <span className="connect-link-text">LinkedIn</span>
              <span className="connect-link-arrow">↗</span>
            </a>
            <a href="mailto:grantburke123@gmail.com" className="connect-link">
              <MailIcon />
              <span className="connect-link-text">Email</span>
              <span className="connect-link-arrow">↗</span>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <p>© {new Date().getFullYear()} Grant Burke. Built with React.</p>
        </footer>
      </main>
    </>
  );
}

export default App;
